import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { App, Button, Popconfirm, Space, Spin, Tag, Tooltip } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import GenericCard from "components/UI/GenericCard/GenericCard";
import AntTable from "components/UI/AntTable";
import DashedTooltip from "components/UI/DashedTooltip";
import StrategyActivityCard from "components/Strategies/Cards/StrategyActivity/StrategyActivityCard";
import FillQualityCard from "components/FillQuality/FillQualityCard";
import CloseOptionTradeModal from "./CloseOptionTradeModal";
import OpenOptionTradeModal from "./OpenOptionTradeModal";
import { formatMoney } from "./optionTradeUtils";
import {
  apiDeleteOptionTrade,
  apiDismissOptionBuy,
  apiGetOptionStrategyDetails,
} from "api/apiOptionStrategies";
import { returnUpDownClassname } from "utils/helpers";
import ConfirmOptionBuyModal from "./ConfirmOptionBuyModal";
import { apiApproveBuyProposal, apiRejectBuyProposal } from "api/apiTrades";
import OptionStrategySettings from "./OptionStrategySettings";
import { useTradingRefresh } from "components/Trading/tradingRefresh";
import {
  formatDay,
  formatIv,
  formatPercent,
  formatPrice,
} from "./optionStrategyUtils";

const contractLabel = (trade) =>
  `${trade.optionTrade?.strikePrice} ${trade.optionTrade?.optionType || "call"}`;

const money = (value) => (
  <span className={returnUpDownClassname(value)}>${formatMoney(value)}</span>
);

const percent = (value) => (
  <span className={returnUpDownClassname(value)}>{formatPercent(value)}</span>
);

// One loaded option strategy (a tab on the Options Strategies page).
const LoadedOptionStrategy = (props) => {
  const { strategyId, snapshot, onChanged } = props;
  const { message } = App.useApp();
  const [strategy, setStrategy] = useState(null);
  // reloads after a change here or anywhere else on the Trading page
  const { refreshKey } = useTradingRefresh();
  const [buying, setBuying] = useState(null);
  const [closing, setClosing] = useState(null);
  const [isLogging, setIsLogging] = useState(false);

  const load = async () => {
    try {
      setStrategy(await apiGetOptionStrategyDetails(strategyId));
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to load strategy");
    }
  };

  // after any change: reload this tab, its activity, and the list above
  const refresh = () => onChanged();

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategyId, refreshKey]);

  if (!strategy) return <Spin />;

  const onDismiss = async (trade) => {
    try {
      await apiDismissOptionBuy(trade._id);
      message.success("Signal dismissed");
      refresh();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to dismiss");
    }
  };

  // semi-automation proposals
  const onDecide = async (apiCall, trade) => {
    try {
      await apiCall(trade._id);
      message.success("Done");
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed");
    } finally {
      refresh();
    }
  };

  const onDeleteTrade = async (trade) => {
    try {
      await apiDeleteOptionTrade(trade._id);
      message.success("Trade deleted");
      refresh();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to delete trade");
    }
  };

  const pendingColumns = [
    { title: "Contract", key: "contract", render: contractLabel },
    {
      title: "Expiration",
      key: "expiration",
      render: (t) => formatDay(t.optionTrade?.expirationDate),
    },
    { title: "Contracts", dataIndex: "advicedBuyAmount" },
    { title: "Ask", dataIndex: "advicedBuyPrice", render: formatPrice },
    {
      title: "Cost",
      dataIndex: "advicedBuyValue",
      render: (v) => `$${formatMoney(v)}`,
    },
    {
      title: (
        <DashedTooltip text="Exit when the underlying closes at or above this (15:50 ET)">
          Target underlying
        </DashedTooltip>
      ),
      key: "target",
      render: (t) =>
        `${formatPrice(t.optionTrade?.targetUnderlyingPrice)} (from ${formatPrice(t.optionTrade?.signalUnderlyingPrice)})`,
    },
    {
      title: (
        <DashedTooltip text="Predicted option profit if the target is hit next day / only at expiration">
          Predicted best / worst
        </DashedTooltip>
      ),
      key: "predicted",
      render: (t) => (
        <>
          {percent(t.optionTrade?.predictedBestProfitPercent)} /{" "}
          {percent(t.optionTrade?.predictedWorstProfitPercent)}
        </>
      ),
    },
    {
      title: "IV",
      key: "iv",
      render: (t) => formatIv(t.optionTrade?.ivAtOpen),
    },
    { title: "Signal", dataIndex: "advicedBuyDate", render: formatDay },
    {
      title: "",
      key: "actions",
      render: (trade) =>
        trade.approval?.status === "awaiting" ? (
          <Space wrap>
            <Tag color="gold">Awaiting approval</Tag>
            <Popconfirm
              title={`Approve buying ${trade.advicedBuyAmount} contracts?`}
              description="AutoTrader will place the order."
              okText="Approve"
              onConfirm={() => onDecide(apiApproveBuyProposal, trade)}
            >
              <Button size="small" type="primary">
                Approve
              </Button>
            </Popconfirm>
            <Popconfirm
              title="Reject this buy?"
              okText="Reject"
              okButtonProps={{ danger: true }}
              onConfirm={() => onDecide(apiRejectBuyProposal, trade)}
            >
              <Button size="small" danger>
                Reject
              </Button>
            </Popconfirm>
          </Space>
        ) : trade.orderStatus === "open-in-progress" ? (
          <Tag color="processing">AutoTrader is buying</Tag>
        ) : (
          <Space>
            {strategy.automated && <Tag color="blue">AutoTrader</Tag>}
            <Button
              size="small"
              type="primary"
              onClick={() => setBuying(trade)}
            >
              Accept
            </Button>
            <Popconfirm
              title="Dismiss this buy signal?"
              onConfirm={() => onDismiss(trade)}
            >
              <Button size="small">Dismiss</Button>
            </Popconfirm>
          </Space>
        ),
    },
  ];

  const openColumns = [
    { title: "Contract", key: "contract", render: contractLabel },
    {
      title: "Expiration",
      key: "expiration",
      render: (t) => formatDay(t.optionTrade?.expirationDate),
    },
    { title: "Contracts", dataIndex: "amount" },
    { title: "Entry", dataIndex: "openRate", render: formatPrice },
    {
      title: (
        <DashedTooltip text="Bid/ask mid of the contract at the last 15:50 run">
          Current
        </DashedTooltip>
      ),
      dataIndex: "currentRate",
      render: formatPrice,
    },
    {
      title: "P&L",
      key: "pnl",
      render: (t) =>
        t.currentRate != null ? (
          <>
            {money(t.profit?.value)} ({percent(t.profit?.perc)})
          </>
        ) : (
          "-"
        ),
    },
    {
      title: "Target underlying",
      key: "target",
      render: (t) => formatPrice(t.optionTrade?.targetUnderlyingPrice),
    },
    { title: "Bars open", dataIndex: "dealDaysOpen" },
    {
      title: "Status",
      key: "status",
      render: (t) =>
        t.orderStatus === "pending-close" ? (
          <Tooltip title={t.closeReason}>
            <Tag color="gold">Exit advised</Tag>
          </Tooltip>
        ) : (
          <Tag color="blue">Open</Tag>
        ),
    },
    {
      title: "",
      key: "actions",
      render: (trade) => (
        <Button size="small" onClick={() => setClosing(trade)}>
          Close
        </Button>
      ),
    },
  ];

  const closedColumns = [
    { title: "Contract", key: "contract", render: contractLabel },
    {
      title: "Opened → Closed",
      key: "dates",
      render: (t) => `${formatDay(t.openDate)} → ${formatDay(t.closeDate)}`,
    },
    { title: "Contracts", dataIndex: "amount" },
    {
      title: "Entry → Exit",
      key: "rates",
      render: (t) => `${formatPrice(t.openRate)} → ${formatPrice(t.closeRate)}`,
    },
    {
      title: "Net P&L",
      key: "net",
      render: (t) => (
        <>
          {money(t.netProfit?.value)} ({percent(t.netProfit?.perc)})
        </>
      ),
      sorter: {
        compare: (a, b) =>
          (a.netProfit?.value || 0) - (b.netProfit?.value || 0),
      },
    },
    {
      title: (
        <DashedTooltip text="Predicted at the buy signal: target hit next day / only at expiration">
          Predicted
        </DashedTooltip>
      ),
      key: "predicted",
      render: (t) => (
        <>
          {percent(t.optionTrade?.predictedBestProfitPercent)} /{" "}
          {percent(t.optionTrade?.predictedWorstProfitPercent)}
        </>
      ),
    },
    {
      title: (
        <DashedTooltip text="Model check: the opening IV priced with the actual underlying at exit. The gap to the actual result is mostly the IV change and the spread">
          Model at exit
        </DashedTooltip>
      ),
      key: "model",
      render: (t) =>
        t.optionTrade?.modelExitPrice != null ? (
          <>
            {formatPrice(t.optionTrade.modelExitPrice)} (
            {percent(t.optionTrade.modelProfitPercent)})
          </>
        ) : (
          "-"
        ),
    },
    {
      title: "Actual",
      key: "actual",
      render: (t) => percent(t.profit?.perc),
    },
    {
      title: "IV open → close",
      key: "iv",
      render: (t) =>
        `${formatIv(t.optionTrade?.ivAtOpen)} → ${formatIv(t.optionTrade?.ivAtClose)}`,
    },
    {
      title: "Reason",
      key: "reason",
      render: (t) => (
        <Tooltip title={t.closeReason}>
          <Tag>{t.closeSignal || "manual"}</Tag>
        </Tooltip>
      ),
    },
    {
      title: "",
      key: "actions",
      render: (trade) =>
        trade.source === "manual" ? (
          <Popconfirm
            title="Delete this trade?"
            onConfirm={() => onDeleteTrade(trade)}
          >
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  const open = [...(strategy.openPositions || [])];

  return (
    <div className="cards-wrapper">
      <OptionStrategySettings strategy={strategy} onSaved={refresh} />

      {!strategy.signalsOnly && strategy.pendingBuys?.length > 0 && (
        <GenericCard
          title={`Buy signals (${strategy.pendingBuys.length})`}
          className="table-card"
          tooltipText="Suggested at the 15:50 ET run. Accept with your real fill, or dismiss. Unanswered signals expire at the next run."
        >
          <AntTable
            rowKey="_id"
            columns={pendingColumns}
            dataSource={strategy.pendingBuys}
            virtual={false}
          />
        </GenericCard>
      )}

      <GenericCard
        title={`Open positions (${open.length})`}
        className="table-card"
      >
        <Button
          size="small"
          icon={<PlusOutlined />}
          style={{ marginBottom: 12 }}
          onClick={() => setIsLogging(true)}
        >
          Log trade manually
        </Button>
        <AntTable
          rowKey="_id"
          columns={openColumns}
          dataSource={open}
          virtual={false}
        />
      </GenericCard>

      <GenericCard
        title={`Closed trades (${strategy.transactions?.length || 0})`}
        className="table-card"
      >
        <AntTable
          rowKey="_id"
          columns={closedColumns}
          dataSource={strategy.transactions || []}
          virtual={false}
        />
      </GenericCard>

      <StrategyActivityCard
        key={`activity-${refreshKey}`}
        selectedStrategy={strategy}
      />

      {strategy.automated && <FillQualityCard strategyId={strategy._id} />}

      {buying && (
        <ConfirmOptionBuyModal
          trade={buying}
          onClose={() => setBuying(null)}
          onSaved={() => {
            setBuying(null);
            refresh();
          }}
        />
      )}
      {closing && (
        <CloseOptionTradeModal
          trade={closing}
          onClose={() => setClosing(null)}
          onSaved={() => {
            setClosing(null);
            refresh();
          }}
        />
      )}
      {isLogging && (
        <OpenOptionTradeModal
          item={{ _id: strategy._id, symbol: strategy.symbol }}
          snapshot={snapshot}
          onClose={() => setIsLogging(false)}
          onSaved={() => {
            setIsLogging(false);
            refresh();
          }}
        />
      )}
    </div>
  );
};

LoadedOptionStrategy.propTypes = {
  strategyId: PropTypes.string.isRequired,
  snapshot: PropTypes.object,
  onChanged: PropTypes.func.isRequired,
};

export default LoadedOptionStrategy;
